import { useQuery } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import * as React from "react";
import { type Control, useForm } from "react-hook-form";
import { bookingStatusOptions, formStructure } from "./constants";
import { useGetFormFields } from "./hooks";
import { type ProcessedField } from "./types";
import { processBookingFieldSettings } from "./utils";
import { createBookingFormSchema, type BookingFormSchema } from "./validation";
import { Combobox } from "@/components/Combobox";
import { type ComboboxOption } from "@/components/Combobox/types";
import { FormDatePicker } from "@/components/FormDatePicker";
import { FormItemWithMessage } from "@/components/FormItemWithMessage";
import { SelectInput } from "@/components/SelectInput";
import { Heading3 } from "@/components/Typography";
import { Button } from "@/components/ui/button";
import { Form, FormField } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { businessUnitUsersQuery } from "@/contacts/ContactForm";
import { graphql, type ResultOf } from "@/graphql";
import { useActiveBusinessUnit } from "@/hooks/use-active-business-unit";
import { removeNothings } from "@/lib/utils/arrays";
import { cn } from "@/lib/utils/common";
import { formatContactLocationLabel, formatName } from "@/lib/utils/formatting";

export type BookingFormMode = "create" | "edit" | "duplicate";

export interface BookingFormProps {
  mode: BookingFormMode;
  onSubmit: (values: BookingFormSchema) => Promise<void>;
  onCancel?: () => void;
  initialData?: Partial<BookingFormSchema>;
}

const getSubmitLabel = (mode: BookingFormMode) => {
  switch (mode) {
    case "create":
    case "duplicate":
      return "Save and Create";
    case "edit":
      return "Save Changes";
    default: {
      const _exhaustiveCheck: never = mode;
      throw new Error(`Unhandled booking form mode: ${_exhaustiveCheck}`);
    }
  }
};

export function BookingForm({ mode, onSubmit, onCancel, initialData }: BookingFormProps) {
  const { activeBusinessUnitId } = useActiveBusinessUnit();

  const {
    data: fieldSettingsData,
    loading: fieldSettingsLoading,
    error: fieldSettingsError,
  } = useQuery(supplyChainBookingFieldSettingsQuery, {
    variables: { id: activeBusinessUnitId || "" },
  });

  const fieldSettings = removeNothings(fieldSettingsData?.supplyChainBookingFieldSettings || []);
  const schema = createBookingFormSchema(fieldSettings);

  const form = useForm<BookingFormSchema>({
    resolver: zodResolver(schema),
    defaultValues: {
      // Basic booking fields
      name: initialData?.name || "",
      status: initialData?.status || "DRAFT",
      plannedShipmentDate: initialData?.plannedShipmentDate || new Date(),
      numberOfHead: initialData?.numberOfHead || undefined,
      note: initialData?.note || "",
      destination: initialData?.destination || null,
      businessAccountManagerId: initialData?.businessAccountManagerId || null,
      source: initialData?.source || null,

      // Metadata fields
      ACCOUNT_NUMBER: initialData?.ACCOUNT_NUMBER || "",
      ACCREDITATIONS: initialData?.ACCREDITATIONS || [],
      BRAND_NOTE: initialData?.BRAND_NOTE || "",
      BREED_NOTE: initialData?.BREED_NOTE || "",
      BROKER: initialData?.BROKER || undefined,
      CERTIFICATE_NUMBER: initialData?.CERTIFICATE_NUMBER || "",
      CONFIRMED: initialData?.CONFIRMED || false,
      DATE_DELIVERED: initialData?.DATE_DELIVERED || undefined,
      DATE_OF_SALE: initialData?.DATE_OF_SALE || undefined,
      DATE_OF_VISIT: initialData?.DATE_OF_VISIT || undefined,
      ESTIMATED_CHOICE_PERCENT: initialData?.ESTIMATED_CHOICE_PERCENT || undefined,
      ESTIMATED_WEIGHT_NOTE: initialData?.ESTIMATED_WEIGHT_NOTE || "",
      FREIGHT_COST: initialData?.FREIGHT_COST || "",
      GRID: initialData?.GRID || undefined,
      LOT_NUMBER: initialData?.LOT_NUMBER || "",
      PEN_NUMBER: initialData?.PEN_NUMBER || "",
      PRICE: initialData?.PRICE || "",
      PROGRAM: initialData?.PROGRAM || undefined,
      PURCHASE_PRICE_PER_CWT: initialData?.PURCHASE_PRICE_PER_CWT || "",
      RISK_MANAGEMENT: initialData?.RISK_MANAGEMENT || false,
      SERVICE_PROVIDED: initialData?.SERVICE_PROVIDED || "",
      SEX_CLASS: initialData?.SEX_CLASS || undefined,
      SHIPPED_WEIGHT: initialData?.SHIPPED_WEIGHT || undefined,
      SHRINK_PERCENT: initialData?.SHRINK_PERCENT || undefined,
      SLIDE: initialData?.SLIDE || "",
      SOURCE_NOTE: initialData?.SOURCE_NOTE || "",
      TRUCKING_COMPANY: initialData?.TRUCKING_COMPANY || undefined,
      VETERINARIAN: initialData?.VETERINARIAN || undefined,
      BASE_WEIGHT: initialData?.BASE_WEIGHT || undefined,
      LABELS: initialData?.LABELS || [],
      CONDITION: initialData?.CONDITION || undefined,
      PROJECTED_OUT_DATE: initialData?.PROJECTED_OUT_DATE || undefined,
      CONDITIONS:
        initialData?.CONDITIONS?.filter(
          (condition): condition is NonNullable<typeof condition> => condition !== null,
        ) || [],
      OWNERSHIP_TYPE: initialData?.OWNERSHIP_TYPE || undefined,
    },
  });

  const handleSubmit = form.handleSubmit((data) => {
    return onSubmit(data);
  });

  const handleCancel = () => {
    form.reset();

    if (onCancel) {
      onCancel();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "-" || e.key === "e") {
      e.preventDefault();
    }
  };

  const metadataSections = processBookingFieldSettings(formStructure, fieldSettings);

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-x-3" noValidate>
        {/* Basic booking fields section - always visible */}
        <div className="col-span-full">
          <Heading3 className="text-sm font-normal text-neutral-500 dark:text-neutral-400">Booking Details</Heading3>
        </div>
        <BasicBookingFields control={form.control} handleKeyDown={handleKeyDown} focusName={mode !== "edit"} />

        {/* Metadata sections - only visible if they have visible fields and query succeeded */}
        {!fieldSettingsLoading && !fieldSettingsError && fieldSettingsData
          ? metadataSections.map((section) => (
              <React.Fragment key={section.id}>
                <div className="col-span-full mt-6">
                  <Heading3 className="text-sm font-normal text-neutral-500 dark:text-neutral-400">
                    {section.title}
                  </Heading3>
                </div>
                <MetadataFields control={form.control} sectionFields={section.fields} />
              </React.Fragment>
            ))
          : null}

        <div className="md:col-span-3 mt-5 flex flex-col md:flex-row md:justify-end gap-3">
          <Button
            disabled={form.formState.isSubmitting}
            type="button"
            variant="outline"
            onClick={handleCancel}
            className="w-full md:w-auto"
          >
            Cancel
          </Button>
          <Button
            isLoading={form.formState.isSubmitting}
            disabled={form.formState.isSubmitting}
            type="submit"
            variant="confirm"
            className="w-full md:w-auto"
          >
            {getSubmitLabel(mode)}
          </Button>
        </div>
      </form>
    </Form>
  );
}

function BasicBookingFields({
  control,
  handleKeyDown,
  focusName,
}: {
  control: Control<BookingFormSchema>;
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  focusName: boolean;
}) {
  const { activeBusinessUnitId, activeBusinessUnitIdInt, activeBusinessUnitCountryCode } = useActiveBusinessUnit();

  const {
    data: usersData,
    loading: userDataLoading,
    error: usersError,
  } = useQuery(businessUnitUsersQuery, {
    variables: { businessUnitId: activeBusinessUnitIdInt },
    skip: !activeBusinessUnitIdInt,
  });

  const userOptions: ComboboxOption[] = removeNothings(usersData?.businessUnitUsers || []).map((businessUnitUser) => {
    return {
      label: formatName({ firstName: businessUnitUser.user.firstName, lastName: businessUnitUser.user.lastName }),
      value: businessUnitUser.user.id,
    };
  });

  const {
    data: supplyChainPartnerLocationData,
    loading: supplyChainPartnerLocationDataLoading,
    error: supplyChainPartnerLocationError,
  } = useQuery(supplyChainPartnerLocationsQuery, {
    variables: {
      businessUnit: activeBusinessUnitId || "",
      status: ["ACTIVE"],
      sortBy: [{ field: "BUSINESS_NAME", desc: false }],
    },
  });

  const supplyChainPartnerLocationOptions = getLocationOptions({
    edges: supplyChainPartnerLocationData?.supplyChainPartners?.edges || [],
    businessUnitCountryCode: activeBusinessUnitCountryCode,
  });

  return (
    <>
      <FormField
        control={control}
        name="name"
        render={({ field }) => (
          <FormItemWithMessage
            label="Name *"
            inputComponent={
              // New and duplicated bookings open with the name ready to type, rather than ~15 Tabs away.
              // Editing keeps the dialog's default focus, as the change is usually further down the form.
              <Input placeholder="Enter booking name" autoFocus={focusName} {...field} />
            }
          />
        )}
      />

      <FormField
        control={control}
        name="numberOfHead"
        render={({ field }) => {
          return (
            <FormItemWithMessage
              label="Number of Head *"
              inputComponent={
                <Input
                  type="number"
                  placeholder="Enter number of head"
                  className="h-10 border-input"
                  min={1}
                  onKeyDown={handleKeyDown}
                  {...field}
                  value={field.value ?? ""}
                />
              }
            />
          );
        }}
      />

      <FormField
        control={control}
        name="status"
        render={({ field }) => (
          <FormItemWithMessage
            label="Status *"
            inputComponent={<SelectInput placeholder="Select status" options={bookingStatusOptions} {...field} />}
          />
        )}
      />

      <FormDatePicker<BookingFormSchema> name="plannedShipmentDate" label="Planned Shipping Date *" isModal />

      <FormField
        control={control}
        name="source"
        render={({ field }) => (
          <FormItemWithMessage
            label="Source"
            inputComponent={
              <Combobox
                type="single"
                loading={supplyChainPartnerLocationDataLoading}
                selected={field.value || ""}
                placeholder="Select source"
                options={supplyChainPartnerLocationOptions}
                isModal
                error={supplyChainPartnerLocationError}
                {...field}
              />
            }
          />
        )}
      />

      <FormField
        control={control}
        name="destination"
        render={({ field }) => (
          <FormItemWithMessage
            label="Destination"
            inputComponent={
              <Combobox
                type="single"
                loading={supplyChainPartnerLocationDataLoading}
                selected={field.value || ""}
                placeholder="Select destination"
                options={supplyChainPartnerLocationOptions}
                isModal
                error={supplyChainPartnerLocationError}
                {...field}
              />
            }
          />
        )}
      />

      <FormField
        control={control}
        name="businessAccountManagerId"
        render={({ field }) => (
          <FormItemWithMessage
            label="Booking Manager"
            inputComponent={
              <Combobox
                type="single"
                loading={userDataLoading}
                placeholder="Select Manager"
                options={userOptions}
                selected={field.value || null}
                isModal
                error={usersError}
                {...field}
              />
            }
          />
        )}
      />

      <FormField
        control={control}
        name="note"
        render={({ field }) => (
          <FormItemWithMessage
            label="Note"
            className="md:col-span-3"
            inputComponent={<Textarea placeholder="Enter note" className="resize-none" {...field} />}
          />
        )}
      />
    </>
  );
}

function MetadataFields({
  control,
  sectionFields,
}: {
  control: Control<BookingFormSchema>;
  sectionFields: ProcessedField[];
}) {
  const fields = useGetFormFields();

  return (
    <>
      {sectionFields.map((field) => {
        if (!fields[field.id]) {
          console.warn(`No render function found for field: ${field.id}`);
          return null;
        }

        const colSpanClass = field.columnSpan && field.columnSpan > 1 ? `md:col-span-${field.columnSpan}` : "";

        return (
          <div key={field.id} className={cn("contents", colSpanClass)}>
            {fields[field.id]({
              control,
              setting: {
                field: field.id,
                isRequired: field.isRequired,
                isVisible: field.isVisible,
              },
            })}
          </div>
        );
      })}
    </>
  );
}

export const getLocationOptions = ({
  edges,
  businessUnitCountryCode,
}: {
  edges: SupplyChainPartnerLocation;
  businessUnitCountryCode?: string | null;
}): ComboboxOption[] => {
  return edges
    .map((edge) => {
      const node = edge?.node;
      const id = node?.defaultLocation?.id || "";
      const label = formatContactLocationLabel({
        businessName: node?.businessName,
        accountNumber: node?.accountNumber,
        address: node?.defaultLocation?.address,
        businessUnitCountryCode,
      });
      return {
        value: id,
        label: label,
      };
    })
    .filter((option) => option.value !== "");
};

export const supplyChainBookingFieldSettingsQuery = graphql(`
  query SupplyChainBookingFieldSettings($id: ID!) {
    supplyChainBookingFieldSettings(businessUnit: $id) {
      isRequired
      field
      isVisible
    }
  }
`);

export const supplyChainPartnerLocationsQuery = graphql(`
  query SupplyChainPartnerLocations(
    $businessUnit: ID!
    $sortBy: [SupplyChainPartnersColumnSort]
    $status: [SupplyChainPartnerStatusEnumType!]
  ) {
    supplyChainPartners(owners: [$businessUnit], sortBy: $sortBy, status: $status) {
      edges {
        node {
          id
          businessName
          accountNumber
          defaultLocation {
            id
            address {
              id
              line1
              line2
              city
              state
              country {
                id
                isoAlpha2
              }
            }
          }
        }
      }
    }
  }
`);

type SupplyChainPartnerLocation = NonNullable<
  NonNullable<ResultOf<typeof supplyChainPartnerLocationsQuery>>["supplyChainPartners"]
>["edges"];
